// Enhanced geocoding service with debugging and multiple providers
export interface GeocodeResult {
  lat: number;
  lng: number;
  confidence?: number;
  provider?: string;
  display_name?: string;
}

export interface GeocodeDebugInfo {
  originalAddress: string;
  cleanedAddress: string;
  attempts: Array<{
    provider: string;
    success: boolean;
    error?: string;
    result?: GeocodeResult;
  }>;
  finalResult?: GeocodeResult;
}

// Address cleaning function
function cleanAddress(address: string): string {
  return address
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s,.-]/g, '') // Remove special characters except common ones
    .replace(/\b(suite|ste|apt|apartment|unit)\s*#?\s*\w+/gi, '') // Remove suite/apt numbers
    .trim();
}

// Nominatim geocoding with enhanced error handling
async function geocodeWithNominatim(address: string): Promise<GeocodeResult | null> {
  try {
    const baseUrl = process.env.REACT_APP_NOMINATIM_BASE_URL || 'https://nominatim.openstreetmap.org';
    const userAgent = process.env.REACT_APP_USER_AGENT || 'AddressRouter/1.0';
    
    // Rate limiting - respect Nominatim's usage policy
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const response = await fetch(
      `${baseUrl}/search?format=json&q=${encodeURIComponent(address)}&limit=3&addressdetails=1&countrycodes=us`,
      {
        headers: {
          'User-Agent': userAgent
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      const location = data[0];
      return { 
        lat: parseFloat(location.lat), 
        lng: parseFloat(location.lon),
        confidence: parseFloat(location.importance || 0),
        provider: 'Nominatim',
        display_name: location.display_name
      };
    }
    
    return null;
  } catch (error) {
    console.error('Nominatim geocoding error:', error);
    throw error;
  }
}

// MapBox geocoding (requires API key)
async function geocodeWithMapBox(address: string): Promise<GeocodeResult | null> {
  const apiKey = process.env.REACT_APP_MAPBOX_API_KEY;
  
  if (!apiKey || apiKey === 'your_mapbox_api_key_here') {
    throw new Error('MapBox API key not configured');
  }
  
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${apiKey}&country=us&limit=1`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const location = data.features[0];
      return {
        lat: location.center[1],
        lng: location.center[0],
        confidence: location.relevance,
        provider: 'MapBox',
        display_name: location.place_name
      };
    }
    
    return null;
  } catch (error) {
    console.error('MapBox geocoding error:', error);
    throw error;
  }
}

// Enhanced geocoding with debugging
export async function geocodeAddressWithDebug(address: string): Promise<{
  result: GeocodeResult | null;
  debug: GeocodeDebugInfo;
}> {
  const debug: GeocodeDebugInfo = {
    originalAddress: address,
    cleanedAddress: cleanAddress(address),
    attempts: []
  };
  
  const providers = [
    { name: 'MapBox', fn: geocodeWithMapBox },
    { name: 'Nominatim', fn: geocodeWithNominatim }
  ];
  
  // Try with cleaned address first
  for (const provider of providers) {
    try {
      const result = await provider.fn(debug.cleanedAddress);
      debug.attempts.push({
        provider: provider.name,
        success: !!result,
        result: result || undefined
      });
      
      if (result) {
        debug.finalResult = result;
        return { result, debug };
      }
    } catch (error) {
      debug.attempts.push({
        provider: provider.name,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
  
  // If cleaned address fails, try original address
  if (debug.cleanedAddress !== debug.originalAddress) {
    for (const provider of providers) {
      try {
        const result = await provider.fn(debug.originalAddress);
        debug.attempts.push({
          provider: provider.name + ' (original)',
          success: !!result,
          result: result || undefined
        });
        
        if (result) {
          debug.finalResult = result;
          return { result, debug };
        }
      } catch (error) {
        debug.attempts.push({
          provider: provider.name + ' (original)',
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }
  
  return { result: null, debug };
}

// Main geocoding function (backward compatible)
export async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
  const { result, debug } = await geocodeAddressWithDebug(address);
  
  // Log debugging info for failed geocodes
  if (!result) {
    console.warn('Geocoding failed for address:', address);
    console.warn('Debug info:', debug);
  }
  
  return result;
}

// Batch geocoding with better error handling and progress reporting
export async function geocodeAddresses(
  addresses: string[],
  onProgress?: (completed: number, total: number, current: string) => void
): Promise<Array<{ 
  address: string; 
  coordinates: GeocodeResult | null; 
  debug?: GeocodeDebugInfo;
  error?: string;
}>> {
  const results = [];
  
  for (let i = 0; i < addresses.length; i++) {
    const address = addresses[i];
    
    if (onProgress) {
      onProgress(i, addresses.length, address);
    }
    
    try {
      const { result, debug } = await geocodeAddressWithDebug(address);
      results.push({ 
        address, 
        coordinates: result,
        debug: !result ? debug : undefined // Only include debug info for failures
      });
    } catch (error) {
      results.push({
        address,
        coordinates: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
    
    // Add delay to respect rate limits
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  return results;
}

// Utility function to analyze geocoding results
export function analyzeGeocodingResults(results: Array<{ address: string; coordinates: GeocodeResult | null; debug?: GeocodeDebugInfo }>) {
  const total = results.length;
  const successful = results.filter(r => r.coordinates).length;
  const failed = results.filter(r => !r.coordinates);
  
  console.log(`\n=== GEOCODING ANALYSIS ===`);
  console.log(`Total addresses: ${total}`);
  console.log(`Successful: ${successful} (${((successful/total)*100).toFixed(1)}%)`);
  console.log(`Failed: ${failed.length} (${((failed.length/total)*100).toFixed(1)}%)`);
  
  if (failed.length > 0) {
    console.log(`\nFailed addresses:`);
    failed.forEach((item, index) => {
      console.log(`${index + 1}. "${item.address}"`);
      if (item.debug) {
        console.log(`   Cleaned: "${item.debug.cleanedAddress}"`);
        console.log(`   Attempts: ${item.debug.attempts.length}`);
        item.debug.attempts.forEach(attempt => {
          console.log(`     - ${attempt.provider}: ${attempt.success ? 'SUCCESS' : 'FAILED'} ${attempt.error ? `(${attempt.error})` : ''}`);
        });
      }
    });
  }
  
  return {
    total,
    successful,
    failed: failed.length,
    successRate: (successful/total)*100,
    failedAddresses: failed.map(f => f.address)
  };
}